import { isAxiosError } from 'axios';

/** Thrown when a mutation targets a resource that no longer exists (HTTP 404). */
export class NotFoundError extends Error {
  /** The user who deleted the row, if known. */
  deletedBy: string | undefined;

  constructor(message = 'Resource not found', deletedBy?: string) {
    super(message);
    this.name = 'NotFoundError';
    this.deletedBy = deletedBy;
  }
}

/** Thrown when a mutation conflicts with a concurrent change (HTTP 409). */
export class ConflictError<TRow = unknown> extends Error {
  /** The current (fresh) version of the row from the server. */
  currentRow: TRow | undefined;

  constructor(message = 'Resource was modified', currentRow?: TRow) {
    super(message);
    this.name = 'ConflictError';
    this.currentRow = currentRow;
  }
}

/**
 * Rethrows Axios 409/404 errors as ConflictError/NotFoundError.
 * If the error doesn't match, it is re-thrown as-is.
 */
export function throwIfApiConflictOrNotFound<TRow = unknown>(
  error: unknown,
  label: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API boundary: raw JSON from axios
  mapRow?: (raw: any) => TRow,
): never {
  if (isAxiosError(error)) {
    if (error.response?.status === 409) {
      const raw = error.response.data?.data?.row;
      throw new ConflictError<TRow>('Row was modified by another user', raw && mapRow ? mapRow(raw) : raw);
    }
    if (error.response?.status === 404) {
      throw new NotFoundError(`The ${label} row no longer exists`, error.response.data?.data?.deletedBy);
    }
  }
  throw error;
}
